import type { JSX, Ref } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import type { ClassNameProp } from '../common-types';
import { Button } from './Button';
import { intl, intlFmt } from '../../lib';
import { Icon } from './Icon';

/**
 * Detects if a breadcrumb label is truncated.
 */
function useBreadcrumbTruncation<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    const update = () => {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    };
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
  }, []);

  return { ref, isTruncated };
}

/**
 * Represents a single breadcrumb item.
 */
export interface BreadcrumbItem {
  /**
   * Unique id of the breadcrumb item.
   */
  id: string;
  /**
   * Visible label of the breadcrumb item.
   */
  label: string;
  /**
   * Optional accessible label for screen readers.
   * If not provided, a default label like "Go to {label}" is used for interactive items.
   */
  'aria-label'?: string;
  /**
   * Click handler for navigation.
   * If provided, the item is rendered as a button.
   */
  onClick?: () => void;
}

/**
 * Props for a {@link Breadcrumbs} component.
 */
export interface BreadcrumbsProps extends ClassNameProp {
  /**
   * Breadcrumb items representing the navigation path.
   */
  items: BreadcrumbItem[];
  /**
   * Aria label for the navigation section.
   *
   */
  'aria-label'?: string;
}

/**
 * Renders a single breadcrumb item.
 */
function BreadcrumbItem({
  item,
  isLast
}: Readonly<{
  item: BreadcrumbItem;
  isLast: boolean;
}>) {
  const { ref, isTruncated } = useBreadcrumbTruncation<HTMLElement>();

  if (isLast) {
    return (
      <span
        ref={ref as Ref<HTMLSpanElement>}
        className="ehs-breadcrumbs-item active"
        aria-current="page"
        title={isTruncated ? item.label : undefined}
      >
        {item.label}
      </span>
    );
  }

  return (
    <Button
      ref={ref as Ref<HTMLButtonElement>}
      variant="tertiary"
      text={item.label}
      title={isTruncated ? item.label : undefined}
      onClick={item.onClick}
      className="ehs-breadcrumbs-button"
      aria-label={item['aria-label'] ?? intlFmt('go-to-x', item.label)}
    />
  );
}

/**
 * Breadcrumb navigation component.
 *
 * Shows the current navigation path and allows navigating back to previous levels.
 * All items except the last are clickable. The last item represents the current page.
 *
 * Long labels are truncated with ellipsis. A tooltip is only shown when the text is truncated.
 *
 * @param items Breadcrumb items in order.
 * @param className Optional additional class name.
 * @param ariaLabel Accessible label for the navigation section.
 * @param props
 * @returns The rendered breadcrumb navigation.
 */
export function Breadcrumbs({
  items,
  className,
  'aria-label': ariaLabel = intl('common.breadcrumbs'),
  ...props
}: Readonly<BreadcrumbsProps>): JSX.Element {
  const navClassName = `ehs-breadcrumbs ${className ?? ''}`.trim();

  return (
    <nav aria-label={ariaLabel} className={navClassName} {...props}>
      <ol className="ehs-breadcrumbs-list">
        {items.map((item, index) => {
          const isLast = index === items.length - 1;

          return (
            <li key={`${item.id}`} className="ehs-breadcrumbs-li">
              <BreadcrumbItem item={item} isLast={isLast} />

              {!isLast && (
                <Icon
                  className="ehs-breadcrumbs-separator"
                  iconName="arrow-right"
                  aria-hidden="true"
                  aria-label={undefined}
                />
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
